(function () {
  'use strict';

  const instances = new Map();

  function measureHeaderOffset() {
    const storeHeader = document.querySelector('store-header');
    if (storeHeader) {
      const bottom = Math.round(storeHeader.getBoundingClientRect().bottom);
      if (bottom > 0) return bottom;
    }

    const headerSection = document.getElementById('shopify-section-header');
    if (headerSection?.offsetHeight > 0) {
      return Math.round(headerSection.offsetHeight);
    }

    const fromVar = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    if (!Number.isNaN(fromVar) && fromVar > 0) return Math.round(fromVar);

    return 0;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function segmentCount(blockCount) {
    if (blockCount <= 1) return 1;
    return 3 * blockCount - 1;
  }

  function segmentProgress(progress, segmentIndex, totalSegments) {
    const size = 1 / totalSegments;
    const start = segmentIndex * size;
    return clamp((progress - start) / size, 0, 1);
  }

  class OfRevealBanner {
    constructor(section) {
      this.section = section;
      this.scrollContainer = section.querySelector('[data-of-reveal-scroll]');
      this.pin = section.querySelector('[data-of-reveal-pin]');
      this.backgrounds = section.querySelectorAll('[data-of-reveal-bg]');
      this.copies = section.querySelectorAll('[data-of-reveal-copy]');
      this.blockCount = this.copies.length;
      this.bgReuse = Array.from(this.backgrounds).map((bg) =>
        bg.hasAttribute('data-of-reveal-bg-reuse')
      );

      if (!this.scrollContainer || !this.pin || !this.blockCount) return;

      this.headerOffset = 0;
      this.ticking = false;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      if (this.prefersReducedMotion) {
        this.section.classList.add('is-reduced-motion');
        return;
      }

      this.init();
    }

    init() {
      this.syncHeaderOffset();
      this.measure();
      this.bindEvents();
      this.onScroll();

      window.requestAnimationFrame(() => {
        this.syncHeaderOffset();
        this.measure();
        this.onScroll();
      });
    }

    syncHeaderOffset() {
      const next = measureHeaderOffset();
      this.headerOffset = next;
      this.section.style.setProperty('--of-rb-header-offset', `${next}px`);
    }

    measure() {
      const pinHeight = this.pin.offsetHeight;
      const segments = segmentCount(this.blockCount);
      const travel = Math.max(0, segments - 1) * pinHeight;
      this.scrollContainer.style.height = `${pinHeight + travel}px`;
      this.onScroll();
    }

    getScrollProgress() {
      const rect = this.scrollContainer.getBoundingClientRect();
      const maxScrollY = Math.max(0, this.scrollContainer.offsetHeight - this.pin.offsetHeight);
      let scrollY = this.headerOffset - rect.top;
      scrollY = clamp(scrollY, 0, maxScrollY);
      return maxScrollY > 0 ? scrollY / maxScrollY : 0;
    }

    getTextOpacity(index, progress, totalSegments) {
      if (this.blockCount <= 1) return 1;

      const segSize = 1 / totalSegments;

      if (index === 0) {
        if (progress < segSize) return 1;
        if (progress < 2 * segSize) {
          return 1 - segmentProgress(progress, 1, totalSegments);
        }
        return 0;
      }

      const fadeInStart = 3 * index * segSize;
      const fadeInEnd = (3 * index + 1) * segSize;
      if (progress < fadeInStart) return 0;
      if (progress < fadeInEnd) {
        return (progress - fadeInStart) / (fadeInEnd - fadeInStart);
      }

      if (index >= this.blockCount - 1) return 1;

      const fadeOutStart = (3 * index + 1) * segSize;
      const fadeOutEnd = (3 * index + 2) * segSize;
      if (progress < fadeOutStart) return 1;
      if (progress < fadeOutEnd) {
        return 1 - (progress - fadeOutStart) / (fadeOutEnd - fadeOutStart);
      }

      return 0;
    }

    getNextDistinctBgIndex(fromIndex) {
      let next = fromIndex + 1;
      while (next < this.blockCount && this.bgReuse[next]) {
        next += 1;
      }
      return next;
    }

    getBackgroundOpacity(index, progress, totalSegments) {
      if (this.blockCount <= 1) return index === 0 ? 1 : 0;
      if (this.bgReuse[index]) return 0;

      const segSize = 1 / totalSegments;
      const fadeInSegment = index === 0 ? 0 : 3 * index - 1;

      if (index > 0 && progress < fadeInSegment * segSize) return 0;

      if (index > 0 && progress < (fadeInSegment + 1) * segSize) {
        return segmentProgress(progress, fadeInSegment, totalSegments);
      }

      const nextDistinct = this.getNextDistinctBgIndex(index);
      if (nextDistinct >= this.blockCount) return 1;

      const coverSegment = 3 * nextDistinct - 1;
      if (progress < (coverSegment + 1) * segSize) return 1;

      return 0;
    }

    update() {
      const progress = this.getScrollProgress();
      const totalSegments = segmentCount(this.blockCount);

      this.copies.forEach((copy, index) => {
        const opacity = this.getTextOpacity(index, progress, totalSegments);
        copy.style.opacity = String(opacity);
        copy.style.visibility = opacity <= 0.01 ? 'hidden' : 'visible';
        copy.style.transform = opacity < 1 ? `translate3d(0, ${(1 - opacity) * 12}px, 0)` : '';
      });

      this.backgrounds.forEach((bg, index) => {
        const opacity = this.getBackgroundOpacity(index, progress, totalSegments);
        bg.style.opacity = opacity <= 0.001 ? '0' : opacity >= 0.999 ? '1' : opacity.toFixed(3);
      });
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    onResize() {
      this.syncHeaderOffset();
      this.measure();
    }

    bindEvents() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.pin);
        this.resizeObserver.observe(this.scrollContainer);
      }

      this.section.querySelectorAll('img, video').forEach((media) => {
        if (media.tagName === 'IMG' && media.complete) return;
        if (media.tagName === 'VIDEO' && media.readyState >= 2) return;
        media.addEventListener(media.tagName === 'VIDEO' ? 'loadeddata' : 'load', this.onResize, {
          once: true,
        });
      });
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();

      this.scrollContainer.style.height = '';
      this.copies.forEach((copy) => {
        copy.style.opacity = '';
        copy.style.visibility = '';
        copy.style.transform = '';
      });
      this.backgrounds.forEach((bg) => {
        bg.style.opacity = '';
        bg.style.zIndex = '';
      });
    }
  }

  function initRevealBanners() {
    document.querySelectorAll('[data-of-reveal-banner]').forEach((section) => {
      const id = section.id;
      if (!id) return;

      if (instances.has(id)) {
        instances.get(id).destroy();
      }

      const instance = new OfRevealBanner(section);
      if (instance.pin) {
        instances.set(id, instance);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRevealBanners);
  } else {
    initRevealBanners();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (
      !event.target.querySelector?.('[data-of-reveal-banner]') &&
      !event.target.matches?.('[data-of-reveal-banner]')
    ) {
      return;
    }
    initRevealBanners();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-of-reveal-banner]') || event.target;
    if (!section?.id || !instances.has(section.id)) return;
    instances.get(section.id).destroy();
    instances.delete(section.id);
  });
})();
